/**
 * useProjetosVisiveis — Jun/2026 · (f) resolução unificada de projetos.
 *
 * Antes desta correção cada tela filtrava os projetos por conta própria:
 *   • Cronograma (Gantt.jsx)      → p.empresa_id === empresaAtual.id || p.empresa_nome === empresaAtual.nome
 *   • Sprint Board               → idem, duplicado
 *   • Projetos / Sprints360Projetos → idem, duplicado
 *   • Detalhamento               → getProjeto(id) da URL, SEM nenhum filtro
 *
 * Consequência real reportada pelo usuário: o projeto abria e editava no
 * Detalhamento e "sumia" ao clicar em Cronograma. Motivos possíveis:
 *   1. `empresaAtual` nasce como a empresa MOCK e só é corrigida depois que o
 *      `app_session` é lido de forma assíncrona — nessa janela o filtro
 *      devolvia zero projetos;
 *   2. `projetoBackendToLocal` não carrega `empresa_nome`, então projetos que
 *      vieram do banco dependiam 100% do `empresa_id` bater exatamente.
 *
 * Insight que simplifica tudo: **o bucket do localStorage e a hidratação do
 * backend JÁ são escopados por tenant** (`s360_state_v1__<workspace_id>` e
 * `resolver_empresa` no backend). Filtrar por empresa na UI é redundante e só
 * introduz modos de falha. Aqui o filtro por empresa passa a ser defensivo:
 * só é aplicado quando os dois lados têm identidade resolvida.
 *
 * O isolamento por empresa cliente da CONSULTORIA continua sendo estrito —
 * ali o filtro é uma regra de negócio, não uma checagem redundante.
 */
import { useMemo } from "react";
import { useTenant } from "@/context/TenantContext";
import { useS360Consult } from "@/context/S360ConsultContext";
import { useSprints360 } from "@/context/Sprints360Context";

export function filtrarProjetosVisiveis(projetos, { empresaAtual, hasConsultoria, empresaConsultAtiva }) {
  return (projetos || []).filter(p => {
    if (hasConsultoria) {
      // Regra de negócio: sem empresa cliente ativa, não exibe nada.
      return !!empresaConsultAtiva && p.empresa_simulada_id === empresaConsultAtiva.id;
    }
    // Modo OFF (Plano Consultoria desligado): mostra TODOS os projetos do tenant —
    // tanto os vinculados a uma empresa cliente da consultoria (`empresa_simulada_id`)
    // quanto os vinculados diretamente à empresa contratante. O bucket já é escopado
    // por workspace, então não há vazamento cross-tenant.
    return true;
  });
}

export function useProjetosVisiveis() {
  const { empresaAtual } = useTenant();
  const { hasConsultoria, empresaAtiva: empresaConsultAtiva } = useS360Consult();
  const { state } = useSprints360();

  return useMemo(
    () => filtrarProjetosVisiveis(state.projetos, { empresaAtual, hasConsultoria, empresaConsultAtiva }),
    [state.projetos, empresaAtual, hasConsultoria, empresaConsultAtiva],
  );
}
